// Global Instructions Rule Applied!
//
// Configuration → Interaction Tracking (admin only).
//
// Which mailboxes Reservoir watches, on which channel, whether each takes part
// in email categorisation, and what the Outlook stamp says in each one.
//
// This is the panel that took hardcoded arrays out of TWO edge functions —
// arrays that disagreed with each other (15 mailboxes for mail, 12 for
// calendar). cfg.tracked_mailboxes is now the source of truth both
// subscription managers read, so adding an inbox is an admin action rather
// than a redeploy.
//
// THREE SWITCHES, and they are not the same kind of thing:
//   Mail / Calendar — separate Graph subscriptions on separate app
//     registrations, hence independent. Turning one OFF deletes that
//     subscription immediately (the delete edge function is called with the
//     mailbox), so capture stops at once — it does not wait for expiry. Mail
//     arriving after that is never captured and the gap cannot be filled, so
//     it is confirmed before being applied.
//   Categorisation  — does the mailbox take part in email categorisation.
//     Mail only; calendar events are not categorised. One email is one
//     inbound_events row however many mailboxes received it, so it is
//     classified when ANY recipient wants it, and stamped only where on.
//
// Chrome copied from EmailCategoriesPanel / InternalAgentsPanel: FormSection +
// AntD Table inside it, per-field autosave, standard Tag vocabulary, and
// refusals rendered inline because AntD's static message renders nowhere in
// this app (antd 5.27 on React 19 without the compat patch).
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Button, Input, Modal, Popconfirm, Select, Switch, Table, Tag, Tooltip, Typography
} from 'antd'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  InteractionTrackingConfigAdminData,
  TrackedMailboxRow
} from '../../../state_manager/data_models/interaction_tracking_config_model.ts'
import {
  EmailCategoryAdminRow,
  OUTLOOK_PRESETS
} from '../../../state_manager/data_models/email_categories_config_model.ts'
import {
  addTrackedMailbox,
  fetchInteractionTrackingConfig,
  removeTrackedMailbox,
  setMailboxCategoryLabel,
  updateTrackedMailbox
} from '../services/interactionTrackingConfigService.ts'
import { fetchEmailCategoriesConfig } from '../services/emailCategoriesConfigService.ts'
import MeetingWatchesSection from './MeetingWatchesSection.tsx'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text } = Typography

const EMPTY: InteractionTrackingConfigAdminData = { mailboxes: [], labelOverrides: [] }

const fmtDate = (v?: string | null): string =>
  v ? new Date(v).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'

export default function InteractionTrackingPanel(): React.ReactElement {
  const token = (): string | null => getAccessToken(store.getState().auth)

  const [data, setData] = useState<InteractionTrackingConfigAdminData>(EMPTY)
  const [categories, setCategories] = useState<EmailCategoryAdminRow[]>([])
  const [loading, setLoading] = useState(true)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [adding, setAdding] = useState<{ mailbox: string; display_name: string } | null>(null)
  const [labelsFor, setLabelsFor] = useState<TrackedMailboxRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // The categories read comes along because the label-override editor needs
      // the taxonomy AND each category's firm-wide label to show as the
      // placeholder an override would replace.
      const [tracking, cats] = await Promise.all([
        fetchInteractionTrackingConfig(token()),
        fetchEmailCategoriesConfig(token())
      ])
      setData(tracking)
      setCategories(cats.categories)
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Failed to load the email tracking configuration'
      setRefusal(text)
      showErrorToast(text)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const run = async (action: () => Promise<string | null | void>, failure: string): Promise<boolean> => {
    setRefusal(null)
    try {
      // A save can succeed AND report that the immediate teardown could not be
      // reached. That is not a failure — the switch is off either way — but it
      // must not pass silently, or capture keeps running after the admin
      // believes they stopped it.
      const warning = await action()
      if (typeof warning === 'string' && warning) setRefusal(warning)
      await load()
      return true
    } catch (e) {
      const text = e instanceof Error ? e.message : failure
      setRefusal(text)
      showErrorToast(text)
      await load()
      return false
    }
  }

  const overridesFor = useMemo(() => {
    const byMailbox: Record<string, Record<string, { label?: string | null; color?: string | null }>> = {}
    data.labelOverrides.forEach((o) => {
      const m = o.tracked_mailbox_category_labels__tracked_mailbox_id
      byMailbox[m] = byMailbox[m] || {}
      byMailbox[m][o.tracked_mailbox_category_labels__email_category_id] = {
        label: o.tracked_mailbox_category_labels__outlook_label,
        color: o.tracked_mailbox_category_labels__outlook_color
      }
    })
    return byMailbox
  }, [data.labelOverrides])

  const mailCount = data.mailboxes.filter((m) => m.tracked_mailboxes__email_tracking_enabled).length
  const calCount = data.mailboxes.filter((m) => m.tracked_mailboxes__event_tracking_enabled).length
  const driftCount = data.mailboxes.filter(
    (m) =>
      !!m.tracked_mailboxes__email_tracking_enabled !== !!m.prog__email_subscription_live ||
      !!m.tracked_mailboxes__event_tracking_enabled !== !!m.prog__event_subscription_live
  ).length

  /** Per-channel subscription state: what is configured vs what Graph is
   *  actually doing. 'pending'/'stopping' mean the manager has not caught up. */
  const subscriptionTag = (label: string, want: boolean, live: boolean): React.ReactElement => {
    if (want && live) return <Tag color='green' style={{ marginInlineEnd: 2 }}>{label}</Tag>
    if (want && !live) return <Tag color='gold' style={{ marginInlineEnd: 2 }}>{label}?</Tag>
    if (!want && live) return <Tag color='gold' style={{ marginInlineEnd: 2 }}>{label}!</Tag>
    return <Tag color='default' style={{ marginInlineEnd: 2 }}>{label}</Tag>
  }

  /** One channel toggle. Switching ON is immediate; switching OFF is confirmed,
   *  because it deletes the subscription and the resulting gap in capture can
   *  never be filled. */
  const channelSwitch = (
    row: TrackedMailboxRow,
    field: 'email_tracking_enabled' | 'event_tracking_enabled',
    on: boolean,
    locked: boolean,
    what: string
  ): React.ReactElement => {
    const save = (next: boolean): void => {
      run(() => updateTrackedMailbox(token(), row.tracked_mailboxes__id, { [field]: next }),
        `Failed to update ${what} tracking`)
    }
    if (!on || locked) {
      return <Switch size='small' checked={on} disabled={locked} onChange={(c) => c && save(true)} />
    }
    return (
      <Popconfirm
        title={`Stop watching ${what} for this mailbox?`}
        description={
          <span style={{ maxWidth: 330, display: 'inline-block' }}>
            The Graph subscription is deleted straight away. Nothing on this channel is captured
            from then on, and that gap can never be filled — only what arrives after you switch it
            back on will appear.
          </span>
        }
        okText={`Stop ${what}`}
        cancelText='Cancel'
        onConfirm={() => save(false)}
      >
        <Switch size='small' checked />
      </Popconfirm>
    )
  }

  const columns = [
    {
      title: 'Mailbox',
      dataIndex: 'tracked_mailboxes__mailbox',
      render: (v: string, row: TrackedMailboxRow) => (
        <div>
          <span className='flex items-center gap-2'>
            <span>{v}</span>
            {row.tracked_mailboxes__is_command_mailbox && (
              <Tooltip title='Staff create deals and prospects by emailing this address. It cannot be untracked here.'>
                <Tag color='purple'>command</Tag>
              </Tooltip>
            )}
          </span>
          <Input
            size='small'
            placeholder='Display name (optional)'
            defaultValue={row.tracked_mailboxes__display_name ?? ''}
            key={`${row.tracked_mailboxes__id}-dn-${row.tracked_mailboxes__display_name ?? ''}`}
            style={{ width: 190, marginTop: 4 }}
            onBlur={(e) => {
              const next = e.target.value.trim()
              if (next !== (row.tracked_mailboxes__display_name ?? '')) {
                run(() => updateTrackedMailbox(token(), row.tracked_mailboxes__id, { display_name: next }),
                  'Failed to update the display name')
              }
            }}
          />
        </div>
      )
    },
    {
      title: (
        <Tooltip title="Watch this mailbox's mail. Switching it off deletes the Graph subscription straight away — mail from then on is never captured.">
          Mail
        </Tooltip>
      ),
      width: 90,
      render: (_: unknown, row: TrackedMailboxRow) =>
        channelSwitch(row, 'email_tracking_enabled', !!row.tracked_mailboxes__email_tracking_enabled,
          row.tracked_mailboxes__is_command_mailbox === true, 'mail')
    },
    {
      title: (
        <Tooltip title="Watch this mailbox's calendar. A separate Graph subscription from mail, so it is switched independently — shared boxes often want mail but no calendar.">
          Calendar
        </Tooltip>
      ),
      width: 90,
      render: (_: unknown, row: TrackedMailboxRow) =>
        channelSwitch(row, 'event_tracking_enabled', !!row.tracked_mailboxes__event_tracking_enabled,
          false, 'calendar')
    },
    {
      title: (
        <Tooltip title='Does this mailbox take part in categorisation. An email to several mailboxes is one record, so it is categorised if ANY recipient has this on — and the Outlook category is written only to the mailboxes that do.'>
          Categorise
        </Tooltip>
      ),
      width: 110,
      render: (_: unknown, row: TrackedMailboxRow) => (
        <Switch
          size='small'
          checked={!!row.tracked_mailboxes__categorization_enabled}
          disabled={!row.tracked_mailboxes__email_tracking_enabled}
          onChange={(checked) =>
            run(() => updateTrackedMailbox(token(), row.tracked_mailboxes__id, { categorization_enabled: checked }),
              'Failed to update categorisation')
          }
        />
      )
    },
    {
      title: (
        <Tooltip title="Send to Outlook: the owner of this mailbox may have agent drafts created in their own Outlook Drafts folder from My Drafts. Needs mail tracking (Reservoir notices the send through the mail subscription) and the global switch under Configuration → Internal Agents. Reservoir never sends.">
          Outlook drafts
        </Tooltip>
      ),
      width: 120,
      render: (_: unknown, row: TrackedMailboxRow) => (
        <Switch
          size='small'
          data-testid='outlook-drafts-switch'
          checked={!!row.tracked_mailboxes__outlook_drafts_enabled}
          disabled={!row.tracked_mailboxes__email_tracking_enabled}
          onChange={(checked) =>
            run(() => updateTrackedMailbox(token(), row.tracked_mailboxes__id, { outlook_drafts_enabled: checked }),
              'Failed to update Outlook drafts')
          }
        />
      )
    },
    {
      title: (
        <Tooltip title="Campaign send: email campaigns with Send type “My mailbox” may be sent from this mailbox, as its owner or a delegate. The message leaves from the mailbox itself and is saved in its Sent Items. Also needs the mailbox channel switched on for the environment. Does not need mail tracking.">
          Campaign send
        </Tooltip>
      ),
      width: 130,
      render: (_: unknown, row: TrackedMailboxRow) => (
        <Switch
          size='small'
          data-testid='campaign-send-switch'
          checked={!!row.tracked_mailboxes__campaign_send_enabled}
          onChange={(checked) =>
            run(() => updateTrackedMailbox(token(), row.tracked_mailboxes__id, { campaign_send_enabled: checked }),
              'Failed to update campaign send')
          }
        />
      )
    },
    {
      title: (
        <Tooltip title='Whether Microsoft Graph is actually pushing this mailbox right now, as opposed to what is configured here. They differ until the subscription manager next runs.'>
          Subscriptions
        </Tooltip>
      ),
      width: 150,
      render: (_: unknown, row: TrackedMailboxRow) => (
        <span className='flex items-center gap-1'>
          {subscriptionTag('mail', !!row.tracked_mailboxes__email_tracking_enabled, !!row.prog__email_subscription_live)}
          {subscriptionTag('cal', !!row.tracked_mailboxes__event_tracking_enabled, !!row.prog__event_subscription_live)}
        </span>
      )
    },
    {
      title: (
        <Tooltip title='When mail tracking was last switched on for this mailbox — so a gap in capture is visible rather than invisible.'>
          Mail since
        </Tooltip>
      ),
      dataIndex: 'tracked_mailboxes__email_tracking_last_enabled_at',
      width: 110,
      render: (v: string | null, row: TrackedMailboxRow) => (
        <Text type={row.tracked_mailboxes__email_tracking_enabled ? undefined : 'secondary'}>
          {fmtDate(v)}
        </Text>
      )
    },
    {
      title: (
        <Tooltip title='Give this mailbox its own name and colour for a category, instead of the firm-wide one.'>
          Labels
        </Tooltip>
      ),
      width: 120,
      render: (_: unknown, row: TrackedMailboxRow) => (
        <Button size='small' onClick={() => setLabelsFor(row)}>
          {row.prog__label_override_count ? `${row.prog__label_override_count} custom` : 'Customise'}
        </Button>
      )
    },
    {
      title: '',
      width: 50,
      render: (_: unknown, row: TrackedMailboxRow) =>
        row.tracked_mailboxes__is_command_mailbox ? null : (
          <Popconfirm
            title='Remove this mailbox?'
            description='It stops being subscribed. Everything it has already captured is kept.'
            okText='Remove'
            cancelText='Cancel'
            onConfirm={() =>
              run(() => removeTrackedMailbox(token(), row.tracked_mailboxes__id),
                'Failed to remove the mailbox')
            }
          >
            <Button size='small' type='text' danger icon={<DeleteOutlined />} />
          </Popconfirm>
        )
    }
  ]

  const activeOverrides = labelsFor ? overridesFor[labelsFor.tracked_mailboxes__id] || {} : {}

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      {refusal && (
        <Alert type='error' showIcon closable message={refusal} onClose={() => setRefusal(null)} />
      )}

      <FormSection
        title='Tracked mailboxes'
        titleColor='blue'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {mailCount} mail · {calCount} calendar · {data.mailboxes.length} mailboxes
            </Text>
            <Button size='small' type='primary' icon={<PlusOutlined />} onClick={() => setAdding({ mailbox: '', display_name: '' })}>
              Add mailbox
            </Button>
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
          </span>
        }
      >
        {driftCount > 0 && (
          <Alert
            type='info'
            showIcon
            style={{ marginBottom: 12 }}
            message={`${driftCount} mailbox${driftCount === 1 ? '' : 'es'} waiting on the subscription manager — Graph is not yet matching what is configured here. It catches up on the manager's next run.`}
          />
        )}
        <Table
          size='small'
          rowKey='tracked_mailboxes__id'
          pagination={false}
          dataSource={data.mailboxes}
          columns={columns}
        />
        <Text type='secondary' style={{ fontSize: 12 }}>
          Adding a mailbox does not start the flow immediately — the subscription manager creates
          the Graph subscription on its next run. Switching a channel off is immediate: that
          subscription is deleted straight away, and anything arriving after that is never captured.
        </Text>
      </FormSection>

      {/* Teams meeting transcript watches (design §8.E) — Graph tracking
          config like the mailboxes above, hence hosted here. */}
      <MeetingWatchesSection />

      <Modal
        title='Add a mailbox'
        open={adding !== null}
        onCancel={() => setAdding(null)}
        okText='Add'
        okButtonProps={{ disabled: !adding?.mailbox?.includes('@') }}
        onOk={async () => {
          if (!adding) return
          const ok = await run(() => addTrackedMailbox(token(), adding.mailbox.trim(), adding.display_name.trim() || undefined),
            'Failed to add the mailbox')
          if (ok) setAdding(null)
        }}
        destroyOnClose
      >
        <div className='flex flex-col gap-3'>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Mailbox address</label>
            <Input
              placeholder='someone@acecap.com'
              value={adding?.mailbox}
              onChange={(e) => setAdding((a) => (a ? { ...a, mailbox: e.target.value } : a))}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Display name (optional)</label>
            <Input
              value={adding?.display_name}
              onChange={(e) => setAdding((a) => (a ? { ...a, display_name: e.target.value } : a))}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Mail is watched from the start; the calendar is not, until you switch it on. Either way
            the Graph subscription is created on the subscription manager's next run, so capture
            begins shortly after — not instantly.
          </Text>
        </div>
      </Modal>

      <Modal
        title={`Labels · ${labelsFor?.tracked_mailboxes__mailbox ?? ''}`}
        open={labelsFor !== null}
        onCancel={() => setLabelsFor(null)}
        footer={null}
        width={640}
        destroyOnClose
      >
        <Text type='secondary' style={{ fontSize: 12 }}>
          Leave a row blank to use the firm-wide name and colour, shown as the placeholder. Clearing
          both removes the override.
        </Text>
        <Table
          size='small'
          rowKey='email_categories__id'
          pagination={false}
          style={{ marginTop: 12 }}
          dataSource={categories}
          columns={[
            { title: 'Category', dataIndex: 'email_categories__label', width: 130 },
            {
              title: 'Name in this mailbox',
              render: (_: unknown, cat: EmailCategoryAdminRow) => (
                <Input
                  size='small'
                  placeholder={cat.email_categories__effective_outlook_label ?? ''}
                  defaultValue={activeOverrides[cat.email_categories__id]?.label ?? ''}
                  key={`${labelsFor?.tracked_mailboxes__id}-${cat.email_categories__id}`}
                  onBlur={(e) => {
                    if (!labelsFor) return
                    const next = e.target.value.trim()
                    const cur = activeOverrides[cat.email_categories__id]
                    if (next === (cur?.label ?? '')) return
                    run(() => setMailboxCategoryLabel(token(), labelsFor.tracked_mailboxes__id,
                      cat.email_categories__id, next || null, cur?.color ?? null),
                      'Failed to update the label override')
                  }}
                />
              )
            },
            {
              title: 'Colour',
              width: 160,
              render: (_: unknown, cat: EmailCategoryAdminRow) => (
                <Select
                  size='small'
                  style={{ width: 140 }}
                  allowClear
                  placeholder='firm default'
                  value={activeOverrides[cat.email_categories__id]?.color ?? undefined}
                  onChange={(next) => {
                    if (!labelsFor) return
                    const cur = activeOverrides[cat.email_categories__id]
                    run(() => setMailboxCategoryLabel(token(), labelsFor.tracked_mailboxes__id,
                      cat.email_categories__id, cur?.label ?? null, next ?? null),
                      'Failed to update the colour override')
                  }}
                  options={OUTLOOK_PRESETS.map((p) => ({
                    value: p.value,
                    label: (
                      <span className='flex items-center gap-2'>
                        <span className='inline-block rounded-sm border border-gray-300'
                          style={{ width: 12, height: 12, backgroundColor: p.hex }} />
                        {p.label}
                      </span>
                    )
                  }))}
                />
              )
            }
          ]}
        />
      </Modal>
    </div>
  )
}
